import { Dropdown, Input, Radio } from "@ab-inbev-maz/compass"
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { FileImagePreview } from "./FileImagePreview";
import { sanitizeRichTextHtml, setSanitizedRichTextContent } from "@/utils/sanitizeRichTextHtml";

const formStyle = {
    '--form-field-text-font-size': '14px',
    '--form-field-padding-x': '16px',
    '--dropdown-options-container-z-index': '2',
    '--form-field-text-color': '#595959'
} as React.CSSProperties;

interface FormField {
    label: string;
    id: string;
    type: 'text' | 'textarea' | 'dropdown' | 'radio' | 'colorPicker' | 'richText';
    placeholder: string;
    options?: Array<{ label: string; value: string; id?: string }>;
    readOnly?: boolean;
}

interface FileConfiguration {
    title: string;
    description: string;
    maxSizeMB: number;
}

export interface FormFieldsStepRef {
    submit: () => void;
}

interface RichTextEditorProps {
    id: string;
    label: string;
    value: string;
    placeholder: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
}

const RICH_TEXT_ACTIONS = [
    { label: 'B', command: 'bold' },
    { label: 'I', command: 'italic' },
    { label: 'U', command: 'underline' },
] as const;

const RichTextEditor = ({ id, label, value, placeholder, onChange, readOnly = false }: RichTextEditorProps) => {
    const editorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sanitizedValue = sanitizeRichTextHtml(value || '');

        if (editorRef.current && editorRef.current.innerHTML !== sanitizedValue) {
            setSanitizedRichTextContent(editorRef.current, sanitizedValue);
        }
    }, [value]);

    const execCommand = (command: string) => {
        if (readOnly) return;
        editorRef.current?.focus();
        document.execCommand(command, false);
        onChange(sanitizeRichTextHtml(editorRef.current?.innerHTML || ''));
    };

    return (
        <div className="grid col-span-2">
            <label htmlFor={id} className="font-semibold text-[#595959] mb-2 text-sm">
                {label}
            </label>
            <div className={`rounded-lg border border-gray-300 overflow-hidden ${readOnly ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex flex-wrap gap-2 border-b border-gray-200 bg-gray-50 p-2">
                    {RICH_TEXT_ACTIONS.map((action) => (
                        <button
                            key={action.command}
                            type="button"
                            className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-[#595959] hover:bg-gray-100"
                            onClick={() => execCommand(action.command)}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
                <div
                    id={id}
                    ref={editorRef}
                    contentEditable={!readOnly}
                    suppressContentEditableWarning
                    className="min-h-[160px] px-4 py-3 text-sm text-[#595959] focus:outline-none"
                    data-placeholder={placeholder}
                    onInput={(event) => {
                        const sanitizedValue = sanitizeRichTextHtml(event.currentTarget.innerHTML);

                        if (event.currentTarget.innerHTML !== sanitizedValue) {
                            setSanitizedRichTextContent(event.currentTarget, sanitizedValue);
                        }

                        onChange(sanitizedValue);
                    }}
                />
            </div>
        </div>
    );
};

interface FormStepProps<T extends z.ZodType> {
    // FirstStep props
    onUpload: (file: File) => void;
    onClearFile: () => void;
    fileConfiguration: FileConfiguration;

    // SecondStep props
    schema: T;
    defaultValues: z.infer<T>;
    fields: FormField[];
    onComplete: (data: z.infer<T>) => void;
    isFetching: boolean;
    formId: string;
    description: string;
    className?: string;
    editMode?: boolean;
}

export const ImageUploadStep = ({ onUpload, onClearFile, fileConfiguration }: Pick<FormStepProps<any>, 'onUpload' | 'onClearFile' | 'fileConfiguration'>) => {
    return (
        <FileImagePreview
            onClear={onClearFile}
            onUpload={onUpload}
            fileConfiguration={fileConfiguration}
        />
    );
};

export const FormFieldsStep = forwardRef<FormFieldsStepRef, Omit<FormStepProps<any>, 'onUpload' | 'onClearFile' | 'fileConfiguration'>>(({
    schema,
    defaultValues,
    fields,
    onComplete,
    isFetching,
    formId,
    description,
    className,
    editMode = false,
}, ref) => {
    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    });

    // Exponer la función submit a través del ref
    useImperativeHandle(ref, () => ({
        submit: () => handleSubmit(onComplete)()
    }), [handleSubmit, onComplete]);

    return (
        <div className={`grid gap-3 ${className}`}>
            <p className="text-sm text-gray-500">{description}</p>

            <form
                id={formId}
                onSubmit={handleSubmit(onComplete)}
                className={`grid gap-3 grid-cols-2 ${isFetching ? 'opacity-50 pointer-events-none' : ''}`}
                style={formStyle}
            >
                {fields.map((field) => {
                    const { label, id, type, placeholder, options, readOnly } = field;
                    const isReadOnly = editMode && readOnly;

                    if (type === "text") {
                        return (
                            <div className="grid" key={`${id}-text`}>
                                <Input
                                    label={label}
                                    id={id}
                                    className={`text-sm ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}
                                    placeholder={placeholder}
                                    readOnly={isReadOnly}
                                    {...register(id)}
                                />
                                {errors[id] && <p className="text-xs text-red-500">{String(errors[id]?.message || '')}</p>}
                            </div>
                        );
                    } else if (type === "textarea") {
                        return (
                            <div className="grid col-span-2" key={`${id}-textarea`}>
                                <label htmlFor={id} className="font-semibold text-[#595959] mb-2 text-sm">
                                    {label}
                                </label>
                                <textarea
                                    id={id}
                                    className={`w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-[#595959] resize-y min-h-[100px] ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}
                                    placeholder={placeholder}
                                    readOnly={isReadOnly}
                                    {...register(id)}
                                />
                                {errors[id] && <p className="text-xs text-red-500">{String(errors[id]?.message || '')}</p>}
                            </div>
                        );
                    } else if (type === "richText") {
                        return (
                            <div key={`${id}-rich-text`} className="col-span-2">
                                <Controller
                                    control={control}
                                    name={id}
                                    render={({ field: renderedField }) => (
                                        <RichTextEditor
                                            id={id}
                                            label={label}
                                            placeholder={placeholder}
                                            value={renderedField.value || ''}
                                            onChange={renderedField.onChange}
                                            readOnly={isReadOnly}
                                        />
                                    )}
                                />
                                {errors[id] && <p className="text-xs text-red-500">{String(errors[id]?.message || '')}</p>}
                            </div>
                        );
                    } else if (type === "dropdown") {
                        return (
                            <div className="grid" key={`${id}-dropdown`}>
                                <Controller
                                    control={control}
                                    name={id}
                                    render={({ field: renderedField }) => (
                                        <Dropdown
                                            label={label}
                                            id={id}
                                            placeholder={{ text: placeholder }}
                                            options={options || []}
                                            {...renderedField}
                                            onChange={(value) => {
                                                const extractedValue = typeof value === 'object' && value !== null && 'value' in value
                                                    ? (value as { value: string }).value
                                                    : value;
                                                renderedField.onChange({ target: { value: extractedValue } });
                                            }}
                                        />
                                    )}
                                />
                                {errors[id] && <p className="text-xs text-red-500">{String(errors[id]?.message || '')}</p>}
                            </div>
                        );
                    } else if (type === "radio") {
                        return (
                            <div className="flex flex-col gap-[0.25rem]" key={`${id}-radio`}>
                                <p className="font-semibold text-[#595959]">{label}</p>
                                <div className="flex gap-6">
                                    {options?.map((option) => (
                                        <Radio
                                            key={option.id || option.value}
                                            label={option.label}
                                            id={option.id || option.value}
                                            {...register(id)}
                                            value={option.value}
                                        />
                                    ))}
                                </div>
                                {errors[id] && (
                                    <p className="text-xs text-red-500">
                                        {String(errors[id]?.message || '')}
                                    </p>
                                )}
                            </div>
                        );
                    } else if (type === "colorPicker") {
                        return (
                            <div className="grid" key={`${id}-colorPicker`}>
                                <label htmlFor={id} className="font-semibold text-[#595959] mb-2 text-sm">
                                    {label}
                                </label>
                                <Controller
                                    control={control}
                                    name={id}
                                    render={({ field: renderedField }) => (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                id={id}
                                                className="h-10 w-20 cursor-pointer border border-gray-300 rounded"
                                                value={renderedField.value || '#000000'}
                                                onChange={(e) => renderedField.onChange(e.target.value)}
                                            />
                                            <Input
                                                id={id}
                                                name=""
                                                className="text-sm flex-1"
                                                placeholder={placeholder}
                                                value={renderedField.value || ''}
                                                onChange={(e) => renderedField.onChange(e.target.value)}
                                                disabled
                                            />
                                        </div>
                                    )}
                                />
                                {errors[id] && <p className="text-xs text-red-500">{String(errors[id]?.message || '')}</p>}
                            </div>
                        )
                    }
                    return null;
                })}

            </form>
        </div>
    );
});
